import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Background,
  Controls,
  MarkerType,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Button, Empty, Space, Tooltip, Typography, theme } from "antd";
import { AimOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ProcessSection, ProcessStatus, ProcessWorkflow } from "../../../api";
import type { FieldRef, Problem, Selection, StageDraft } from "../types";
import { CanvasContext, type CanvasHandlers, type DragPayload } from "./CanvasContext";
import StageNode from "./StageNode";
import { LaneNode, PillNode } from "./LaneNodes";
import { edgeTypes } from "./edges";
import {
  computeLayout,
  indexAtY,
  layoutWidth,
  NODE_WIDTH,
  PILL_WIDTH,
  SPINE_X,
} from "./layout";
import { statusByKind } from "../lifecycle";

/* Node and edge type maps are module constants: React Flow remounts every node
   when the map identity changes, which loses selection and makes dragging
   jump. */
const nodeTypes = { stage: StageNode, lane: LaneNode, pill: PillNode };

/** Below this the cards stop being readable, so the canvas scrolls instead. */
const LEGIBLE_ZOOM = 0.8;

type Props = {
  stages: StageDraft[];
  sections: Record<number, ProcessSection>;
  workflows: ProcessWorkflow[];
  statuses: ProcessStatus[];
  fields: FieldRef[];
  problems: Problem[];
  simulation: Record<string, boolean> | null;
  selection: Selection;
  onSelect: (selection: Selection) => void;
  onReorderStages: (from: number, to: number) => void;
  onPlaceItem: (
    kind: "form" | "workflow",
    itemId: number,
    toStageKey: string | null,
    index: number
  ) => void;
  onAddStage: (kind: string, atIndex: number) => void;
  onRemoveStage: (key: string) => void;
  onAddWorkflow: (stageKey: string) => void;
  onCreateSection: (stageKey: string) => void;
  /** Opens the shape picker — the way out of an empty process. */
  onOpenPresets: () => void;
};

const Canvas = (props: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [dragging, setDragging] = useState<DragPayload>(null);
  const { fitView, setViewport } = useReactFlow();
  const paneRef = useRef<HTMLDivElement>(null);
  // Lane bands stretch to the visible canvas, so they read as phases the whole
  // process passes through rather than boxes floating on the page. Measured,
  // because the pane's width depends on whether the inspector is docked.
  const [paneWidth, setPaneWidth] = useState(0);
  useEffect(() => {
    const node = paneRef.current;
    if (!node) return;
    const measure = () =>
      // Divided by the zoom the canvas opens at: node positions are in flow
      // units, and at 0.8 a 1200px pane shows 1500 units of content.
      setPaneWidth(node.getBoundingClientRect().width / LEGIBLE_ZOOM);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(
    () => computeLayout(props.stages, props.statuses, paneWidth),
    [props.stages, props.statuses, paneWidth]
  );

  const startStatus = statusByKind(props.statuses, "Draft");
  const endStatus =
    statusByKind(props.statuses, "Closed") ?? statusByKind(props.statuses, "Active");

  const { nodes, edges } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const width = layoutWidth(layout);

    // Lanes first, so every other node draws over them. They are not
    // selectable or draggable: a band is a reading of the model, not a thing
    // you can move.
    layout.lanes.forEach((lane, i) => {
      nodes.push({
        id: `lane:${i}`,
        type: "lane",
        position: { x: 24, y: lane.y },
        data: {
          statusName: lane.status?.name,
          kind: lane.status?.kind ?? "Other",
          width: width - 48,
          height: lane.height,
          steps: lane.to - lane.from + 1,
        },
        draggable: false,
        selectable: false,
        zIndex: 0,
      });
    });

    const spineCentre = SPINE_X + NODE_WIDTH / 2;

    nodes.push({
      id: "start",
      type: "pill",
      position: { x: spineCentre - PILL_WIDTH / 2, y: layout.startY },
      data: {
        label: startStatus ? t(startStatus.name) : t("Start"),
        sub: t("a case is raised"),
        tone: "start",
      },
      draggable: false,
      selectable: false,
      zIndex: 2,
    });

    layout.stages.forEach((laid) => {
      nodes.push({
        id: `stage:${laid.stage.key}`,
        type: "stage",
        position: { x: laid.x, y: laid.y },
        data: { stage: laid.stage, index: laid.index, total: layout.stages.length },
        selected:
          props.selection?.type === "stage" &&
          props.selection.key === laid.stage.key,
        zIndex: 3,
      });
    });

    nodes.push({
      id: "end",
      type: "pill",
      position: { x: spineCentre - PILL_WIDTH / 2, y: layout.endY },
      data: {
        label: endStatus ? t(endStatus.name) : t("Finished"),
        sub: t("the case is done"),
        tone: "end",
      },
      draggable: false,
      selectable: false,
      zIndex: 2,
    });

    // The spine: start -> every step in order -> end. Each gap carries the
    // control that inserts a step *there*.
    const chain = [
      "start",
      ...layout.stages.map((s) => `stage:${s.stage.key}`),
      "end",
    ];
    for (let i = 0; i < chain.length - 1; i += 1) {
      edges.push({
        id: `spine:${chain[i]}->${chain[i + 1]}`,
        source: chain[i],
        target: chain[i + 1],
        type: "insert",
        // The gap after the start pill inserts at 0, and so on down.
        data: { insertAt: i },
        markerEnd: { type: MarkerType.ArrowClosed, color: token.colorBorder },
      });
    }

    // A bypass around every step that does not always run.
    layout.stages.forEach((laid) => {
      if (!laid.conditional) return;
      const before = chain[laid.index]; // start pill or the previous step
      const after = chain[laid.index + 2];
      if (!before || !after) return;
      edges.push({
        id: `bypass:${laid.stage.key}`,
        source: before,
        target: after,
        type: "bypass",
        data: {
          channel: laid.channel,
          skipped: props.simulation
            ? props.simulation[laid.stage.key] === false
            : false,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: token.colorTextQuaternary,
        },
      });
    });

    return { nodes, edges };
  }, [layout, props.selection, props.simulation, startStatus, endStatus, t, token]);

  const handlers: CanvasHandlers = useMemo(
    () => ({
      sections: props.sections,
      workflows: props.workflows,
      fields: props.fields,
      problems: props.problems,
      selection: props.selection,
      simulation: props.simulation,
      onSelect: props.onSelect,
      onPlaceItem: props.onPlaceItem,
      onAddStage: props.onAddStage,
      onRemoveStage: props.onRemoveStage,
      onAddWorkflow: props.onAddWorkflow,
      onCreateSection: props.onCreateSection,
      dragging,
      setDragging,
    }),
    [props, dragging]
  );

  /**
   * Where the canvas opens.
   *
   * Fitting the whole process on screen is the obvious default and it is wrong
   * for anything long: a nine-step process fitted into the pane at 24% zoom,
   * where the cards are grey smudges and the labels are unreadable — a picture
   * of a process rather than an editor. So it fits only when fitting still
   * leaves the cards legible; otherwise it opens at the top, at full size, and
   * the person scrolls. The Fit button is always there for the overview.
   */
  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current || layout.stages.length === 0) return;
    openedRef.current = true;
    const pane = paneRef.current?.getBoundingClientRect();
    if (!pane || pane.height === 0) return;
    const contentHeight = layout.height;
    const contentWidth = layoutWidth(layout);
    const wouldFitAt = Math.min(
      (pane.width * 0.9) / contentWidth,
      (pane.height * 0.9) / contentHeight
    );
    if (wouldFitAt >= LEGIBLE_ZOOM) {
      fitView({ padding: 0.12, maxZoom: 1 });
    } else {
      setViewport({ x: 24, y: 16, zoom: LEGIBLE_ZOOM });
    }
  }, [layout, fitView, setViewport]);

  const onNodeClick = useCallback(
    (_: unknown, node: Node) => {
      if (node.id.startsWith("stage:")) {
        props.onSelect({ type: "stage", key: node.id.slice("stage:".length) });
      }
    },
    [props]
  );

  /** Dropping a node reorders the process; the position itself is discarded. */
  const onNodeDragStop = useCallback(
    (_: unknown, node: Node) => {
      if (!node.id.startsWith("stage:")) return;
      const key = node.id.slice("stage:".length);
      const from = props.stages.findIndex((s) => s.key === key);
      if (from < 0) return;
      const to = indexAtY(layout, node.position.y + 20, from);
      if (to !== from) props.onReorderStages(from, to);
    },
    [props, layout]
  );

  if (props.stages.length === 0) {
    /* A template with no steps is not broken -- it runs the *legacy* path,
       where every section is open at once and sign-offs run in their own
       order. Most templates in this deployment are still like that. Saying so
       matters: the previous empty state said only "no steps yet", which reads
       as an error on a template that has been working for years, and gave no
       hint that adding one step changes how the whole thing behaves. */
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 520, textAlign: "center" }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  {t("This template has no steps yet.")}
                </div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t(
                    "It still works: every section is open at once and sign-offs run in their own order. Adding steps is what lets you say who fills what, and when — and lets a step apply only sometimes."
                  )}
                </Typography.Text>
              </div>
            }
          >
            <Space wrap style={{ marginTop: 8 }}>
              <Button type="primary" onClick={props.onOpenPresets}>
                {t("Start from a shape")}
              </Button>
              <Button onClick={() => props.onAddStage("DataEntry", 0)}>
                {t("Add a step people fill in")}
              </Button>
              <Button onClick={() => props.onAddStage("Approval", 0)}>
                {t("Add a step people sign off")}
              </Button>
            </Space>
          </Empty>
        </div>
      </div>
    );
  }

  return (
    // The provider wraps ReactFlow rather than sitting inside it. React Flow
    // renders nodes and edges into its own subtree, which is a *sibling* of
    // whatever children you pass it — so a provider placed among those children
    // is not an ancestor of any node, and every node's `useCanvas()` throws.
    <CanvasContext.Provider value={handlers}>
      <div ref={paneRef} style={{ position: "relative", height: "100%" }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={onNodeClick}
          onNodeDragStop={onNodeDragStop}
          onPaneClick={() => props.onSelect(null)}
          minZoom={0.2}
          maxZoom={1.5}
          nodesConnectable={false}
          edgesFocusable={false}
          deleteKeyCode={null}
          // Dropping a chip outside any step takes it out of the process, which
          // is how something gets back to the tray without a menu.
          onDragOver={(e) => {
            if (dragging) e.preventDefault();
          }}
          onDrop={() => {
            if (!dragging) return;
            props.onPlaceItem(dragging.kind, dragging.id, null, -1);
            setDragging(null);
          }}
          proOptions={{ hideAttribution: false }}
        >
          <Background gap={16} color={token.colorBorderSecondary} />
          <Controls showInteractive={false} />
        </ReactFlow>

        <Tooltip title={t("Fit the whole process on screen")}>
          <Button
            size="small"
            icon={<AimOutlined />}
            onClick={() => fitView({ padding: 0.15, duration: 300 })}
            style={{ position: "absolute", top: 8, right: 8, zIndex: 5 }}
          />
        </Tooltip>
      </div>
    </CanvasContext.Provider>
  );
};

/**
 * The process as an editable canvas.
 *
 * This is the primary surface of the designer, not a picture of it. Everything
 * that shapes the spine happens here: steps are inserted between their
 * neighbours, dragged into a new order, filled by dropping sections and
 * sign-offs onto them, and opened for editing by clicking them. The outline
 * remains as a second reading for long processes and for keyboard use.
 *
 * The layout is hand-rolled rather than handed to a graph library because the
 * process genuinely is a sequence: a conditional step is not a fork into two
 * parallel paths, it is an inline step that some cases skip. So the drawing is
 * a spine with bypass channels arcing around the steps that do not always run
 * -- which is exactly what the engine does, and what a generic auto-layout
 * would obscure.
 */
const ProcessCanvas = (props: Props) => (
  <ReactFlowProvider>
    <Canvas {...props} />
  </ReactFlowProvider>
);

export default ProcessCanvas;
